/**
 * KEPT FILES (the perf overhaul, Stage 1): a small stored file - a handout's thumbnail - fetched once per page.
 *
 * A protected PocketBase file is served at a URL carrying a file token that is renewed every 90 s, and every mount
 * of a thumbnail (the drawer opened, a chat line scrolled back) asked for its URL again: a new token, a new URL, a
 * new download - about a hundred per thumbnail in an evening. A file's ref never changes (a new upload is a new
 * ref), so the bytes are fetched once and the same object URL is handed to every later mount.
 *
 * A download that fails (a refusal, a backend whose URLs the page may not fetch) answers with the plain URL and keeps
 * nothing, so the image still loads the old way and the next mount tries again. Not for full files: those can be
 * large, and a page keeps every kept file until it is closed.
 */
import { diag, errorSummary } from '../diagnostics/log';

const kept = new Map<string, Promise<string>>();

async function download(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`file answered ${res.status}`);
  return URL.createObjectURL(await res.blob());
}

/** An object URL for the file `ref` (resolved to a URL by `resolve` the first time only). */
export async function keptFileUrl(ref: string, resolve: (ref: string) => Promise<string>): Promise<string> {
  const held = kept.get(ref);
  if (held) return held;
  const url = await resolve(ref);
  // What is kept is the answer WITH its fallback, so a mount that arrives while the download is out gets the plain
  // URL too, not the refusal.
  const answer = download(url).catch((e: unknown) => {
    // The plain URL still loads where the page may not fetch it; nothing kept, so a later mount tries afresh.
    diag.warn('files', 'a thumbnail could not be kept - loading it by its URL', errorSummary(e));
    kept.delete(ref);
    return url;
  });
  kept.set(ref, answer);
  return answer;
}

/** Forget every kept file (tests; nothing in the app needs it). */
export function forgetKeptFiles(): void {
  kept.clear();
}
